// src/components/advisor/AdvisorCockpit.jsx
// Cockpit quotidien de l'Art Advisor (refonte dashboard §3–§4 + DA §12).
// Ordre : en-tête métier + action principale → priorités de la semaine → KPIs
// opérationnels → clients à suivre. Toutes les valeurs proviennent de la RPC
// get_advisor_dashboard_summary (données réelles, aucune tendance statique).
import React from "react";
import {
  Plus,
  UserPlus,
  FolderOpen,
  FileText,
  ArrowRight,
  AlertTriangle,
  PackageCheck,
  Receipt,
  Bell,
  ChevronRight,
  Sparkles,
  Clock3,
  X,
  Layers,
} from "lucide-react";

/* ── Palette ADN ARTKIV (§12) ─────────────────────────────── */
const C = {
  ink: "#1A1A1A",
  cream: "#FAFAF8",
  white: "#FFFFFF",
  stone: "#E5E3DF",
  gray: "#6B6B6B",
  gold: "#C9A961",
  goldDark: "#8B7355",
  // fonctionnels très désaturés
  successFg: "#2c5f2d", successBg: "#f1f6f1",
  attnFg: "#8a6a2f",    attnBg: "#fbf6ea",
  lateFg: "#8f3a44",    lateBg: "#f8eef0",
  infoFg: "#3a4a6b",    infoBg: "#eef1f6",
};
const SERIF = "Georgia, 'Times New Roman', serif";
const SANS = "system-ui, -apple-system, 'Segoe UI', sans-serif";

/* ── Copie FR / EN / DE (module autoportant) ──────────────── */
const COPY = {
  fr: {
    hello: "Bonjour",
    helloNoName: "Bonjour",
    priorities: "voici vos priorités",
    subtitle: "ARTKIV a regroupé les actions, livrables et opportunités de vos clients.",
    createMission: "Créer une mission",
    inviteClient: "Inviter un client",
    openCollection: "Ouvrir une collection",
    produceReport: "Produire un rapport",
    importFiles: "Importer des fichiers",
    weekPriorities: "Priorités de la semaine",
    unlinkedDossiers: (n) => `${n} dossier${n > 1 ? "s" : ""} client${n > 1 ? "s" : ""} pas encore relié${n > 1 ? "s" : ""}`,
    noPriorities: "Rien d’urgent cette semaine",
    noPrioritiesSub: "Créez une mission ou partez d’un besoin détecté dans une collection.",
    kpiTitle: "Suivi de l’activité",
    opportunitiesTitle: "Besoins détectés à vérifier",
    opportunitiesLead: "Des données de vos collections suggèrent des missions possibles — à valider par vous.",
    proposeMission: "Proposer la mission",
    createDraft: "Créer un brouillon",
    ignore: "Ignorer",
    remindMe: "Me le rappeler",
    clientsTitle: "Clients à suivre",
    noClients: "Aucun client à suivre",
    noClientsSub: "Invitez un client pour centraliser sa collection et préparer vos premiers livrables.",
    reassurance: "ARTKIV prépare et organise l’information. Vous gardez la décision, la relation client et vos honoraires.",
    dueOn: "Échéance",
    noDue: "Sans échéance",
    overdueLabel: "en retard",
    clientFallback: "Client",
    missionsOpen: (n) => `${n} mission${n > 1 ? "s" : ""} en cours`,
    colsArtworks: (c, a) => `${c} collection${c > 1 ? "s" : ""} · ${a} œuvre${a > 1 ? "s" : ""}`,
    nextDue: "Prochaine échéance",
    kinds: {
      overdue:     { label: "Échéance dépassée", cta: "Continuer" },
      deliverable: { label: "Livrable prêt",     cta: "Ouvrir" },
      to_invoice:  { label: "Mission terminée non facturée", cta: "Marquer facturée" },
      follow_up:   { label: "Relance client",    cta: "Relancer" },
    },
    kpis: {
      missions_in_progress: "Missions en cours",
      deliverables_ready: "Livrables à envoyer",
      ready_to_invoice: "Missions à facturer",
      clients_to_follow_up: "Clients à relancer",
      artworks_requiring_action: "Œuvres à traiter",
    },
  },
  en: {
    hello: "Hello",
    helloNoName: "Hello",
    priorities: "here are your priorities",
    subtitle: "ARTKIV has gathered your clients’ actions, deliverables and opportunities.",
    createMission: "Create a mission",
    inviteClient: "Invite a client",
    openCollection: "Open a collection",
    produceReport: "Produce a report",
    importFiles: "Import files",
    weekPriorities: "This week’s priorities",
    unlinkedDossiers: (n) => `${n} client file${n > 1 ? "s" : ""} not yet linked`,
    noPriorities: "Nothing urgent this week",
    noPrioritiesSub: "Create a mission or start from a need detected in a collection.",
    kpiTitle: "Activity overview",
    opportunitiesTitle: "Detected needs to review",
    opportunitiesLead: "Signals in your collections suggest possible missions — for you to validate.",
    proposeMission: "Propose the mission",
    createDraft: "Create a draft",
    ignore: "Dismiss",
    remindMe: "Remind me",
    clientsTitle: "Clients to follow up",
    noClients: "No client to follow up",
    noClientsSub: "Invite a client to centralise their collection and prepare your first deliverables.",
    reassurance: "ARTKIV prepares and organises the information. You keep the decision, the client relationship and your fees.",
    dueOn: "Due",
    noDue: "No deadline",
    overdueLabel: "overdue",
    clientFallback: "Client",
    missionsOpen: (n) => `${n} open mission${n > 1 ? "s" : ""}`,
    colsArtworks: (c, a) => `${c} collection${c > 1 ? "s" : ""} · ${a} artwork${a > 1 ? "s" : ""}`,
    nextDue: "Next deadline",
    kinds: {
      overdue:     { label: "Overdue", cta: "Continue" },
      deliverable: { label: "Deliverable ready", cta: "Open" },
      to_invoice:  { label: "Completed, not invoiced", cta: "Mark invoiced" },
      follow_up:   { label: "Client follow-up", cta: "Follow up" },
    },
    kpis: {
      missions_in_progress: "Missions in progress",
      deliverables_ready: "Deliverables to send",
      ready_to_invoice: "Missions to invoice",
      clients_to_follow_up: "Clients to follow up",
      artworks_requiring_action: "Artworks needing action",
    },
  },
  de: {
    hello: "Guten Tag",
    helloNoName: "Guten Tag",
    priorities: "hier Ihre Prioritäten",
    subtitle: "ARTKIV hat die Aufgaben, Ergebnisse und Chancen Ihrer Kunden zusammengeführt.",
    createMission: "Auftrag erstellen",
    inviteClient: "Kunde einladen",
    openCollection: "Sammlung öffnen",
    produceReport: "Bericht erstellen",
    importFiles: "Dateien importieren",
    weekPriorities: "Prioritäten der Woche",
    unlinkedDossiers: (n) => `${n} Kundenakte${n > 1 ? "n" : ""} noch nicht verknüpft`,
    noPriorities: "Diese Woche nichts Dringendes",
    noPrioritiesSub: "Erstellen Sie einen Auftrag oder gehen Sie von einem erkannten Bedarf aus.",
    kpiTitle: "Aktivitätsübersicht",
    opportunitiesTitle: "Erkannte Bedürfnisse prüfen",
    opportunitiesLead: "Signale aus Ihren Sammlungen legen mögliche Aufträge nahe — von Ihnen zu prüfen.",
    proposeMission: "Auftrag vorschlagen",
    createDraft: "Entwurf erstellen",
    ignore: "Ignorieren",
    remindMe: "Später erinnern",
    clientsTitle: "Kunden im Blick",
    noClients: "Kein Kunde zu betreuen",
    noClientsSub: "Laden Sie einen Kunden ein, um seine Sammlung zu bündeln und erste Ergebnisse vorzubereiten.",
    reassurance: "ARTKIV bereitet die Informationen auf und ordnet sie. Entscheidung, Kundenbeziehung und Honorare bleiben bei Ihnen.",
    dueOn: "Fällig",
    noDue: "Ohne Frist",
    overdueLabel: "überfällig",
    clientFallback: "Kunde",
    missionsOpen: (n) => `${n} laufende${n > 1 ? "" : "r"} Auftrag${n > 1 ? "e" : ""}`,
    colsArtworks: (c, a) => `${c} Sammlung${c > 1 ? "en" : ""} · ${a} Werk${a > 1 ? "e" : ""}`,
    nextDue: "Nächste Frist",
    kinds: {
      overdue:     { label: "Frist überschritten", cta: "Fortsetzen" },
      deliverable: { label: "Ergebnis bereit", cta: "Öffnen" },
      to_invoice:  { label: "Abgeschlossen, nicht fakturiert", cta: "Als fakturiert markieren" },
      follow_up:   { label: "Kundennachfassung", cta: "Nachfassen" },
    },
    kpis: {
      missions_in_progress: "Laufende Aufträge",
      deliverables_ready: "Zu sendende Ergebnisse",
      ready_to_invoice: "Zu fakturierende Aufträge",
      clients_to_follow_up: "Nachzufassende Kunden",
      artworks_requiring_action: "Zu bearbeitende Werke",
    },
  },
};

const KIND_STYLE = {
  overdue:     { fg: C.lateFg,   bg: C.lateBg,   icon: AlertTriangle },
  deliverable: { fg: C.infoFg,   bg: C.infoBg,   icon: PackageCheck },
  to_invoice:  { fg: C.goldDark, bg: C.attnBg,   icon: Receipt },
  follow_up:   { fg: C.attnFg,   bg: C.attnBg,   icon: Bell },
};

const KPI_ORDER = ["missions_in_progress", "deliverables_ready", "ready_to_invoice", "clients_to_follow_up"];

function pick(lang) {
  const l = String(lang || "fr").slice(0, 2).toLowerCase();
  return COPY[l] || COPY.fr;
}

function fmtDate(value, lang) {
  if (!value) return null;
  try {
    const l = String(lang || "fr").slice(0, 2);
    const loc = l === "de" ? "de-DE" : l === "en" ? "en-GB" : "fr-FR";
    return new Date(value).toLocaleDateString(loc, { day: "2-digit", month: "short" });
  } catch {
    return null;
  }
}

const cardBase = {
  background: C.white,
  border: `1px solid ${C.stone}`,
  borderRadius: 8,
  boxShadow: "0 1px 2px rgba(26,26,26,0.03)",
};

export default function AdvisorCockpit({
  summary,
  loading = false,
  firstName = "",
  lang = "fr",
  opportunities = [],
  artworksRequiringAction = null,
  onCreateMission,
  onInviteClient,
  onOpenCollection,
  onProduceReport,
  onImportFiles,
  onKpiClick,
  onPriorityClick,
  onClientClick,
  onProposeMission,
  onDraftMission,
  onDismissOpportunity,
  onSnoozeOpportunity,
}) {
  const tx = pick(lang);
  const kpis = summary?.kpis || {};
  const priorities = Array.isArray(summary?.priorities) ? summary.priorities : [];
  const clients = Array.isArray(summary?.clients_to_watch) ? summary.clients_to_watch : [];
  const opps = Array.isArray(opportunities) ? opportunities : [];
  const unlinked = summary?.unlinked_dossiers || 0;

  const greetName = (firstName || "").trim();

  return (
    <div style={{ marginBottom: 28 }}>
      {/* ═══ En-tête métier + action principale ═══ */}
      <div style={{ display: "flex", justifyContent: "space-between", gap: 20, alignItems: "flex-start", flexWrap: "wrap", marginBottom: 24 }}>
        <div style={{ minWidth: 0 }}>
          <h1 style={{ fontFamily: SERIF, fontSize: 28, fontWeight: 400, color: C.ink, margin: 0, letterSpacing: "-0.02em", lineHeight: 1.2 }}>
            {greetName ? `${tx.hello} ${greetName} — ${tx.priorities}` : `${tx.helloNoName} — ${tx.priorities}`}
          </h1>
          <p style={{ margin: "8px 0 0", fontSize: 14, color: C.gray, fontFamily: SANS, lineHeight: 1.6, maxWidth: 620 }}>
            {tx.subtitle}
          </p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end" }}>
          <button
            type="button"
            onClick={onCreateMission}
            style={{
              display: "inline-flex", alignItems: "center", gap: 8,
              padding: "11px 18px", background: C.ink, color: C.white,
              border: "none", borderRadius: 8, fontFamily: SANS, fontSize: 14,
              fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
            }}
          >
            <Plus size={17} strokeWidth={2} /> {tx.createMission}
          </button>
          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", justifyContent: "flex-end" }}>
            <SecondaryAction icon={Layers} label={tx.importFiles} onClick={onImportFiles} />
            <SecondaryAction icon={UserPlus} label={tx.inviteClient} onClick={onInviteClient} />
            <SecondaryAction icon={FolderOpen} label={tx.openCollection} onClick={onOpenCollection} />
            <SecondaryAction icon={FileText} label={tx.produceReport} onClick={onProduceReport} />
          </div>
        </div>
      </div>

      {/* ═══ Priorités de la semaine ═══ */}
      <div style={{ marginBottom: 24 }}>
        <SectionEyebrow>{tx.weekPriorities}</SectionEyebrow>
        {unlinked > 0 && (
          <div style={{ ...cardBase, display: "flex", alignItems: "center", gap: 14, padding: "12px 14px", borderLeft: `3px solid ${C.attnFg}`, marginBottom: 8 }}>
            <div style={{ width: 34, height: 34, marginLeft: 0, borderRadius: 7, background: C.attnBg, color: C.attnFg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <UserPlus size={17} strokeWidth={1.8} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14.5, color: C.ink, fontFamily: SERIF, lineHeight: 1.3 }}>{tx.unlinkedDossiers(unlinked)}</div>
            </div>
            <button type="button" onClick={onInviteClient}
              style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "7px 12px", borderRadius: 7, background: C.cream, border: `1px solid ${C.stone}`, color: C.ink, fontFamily: SANS, fontSize: 12.5, fontWeight: 600, cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap" }}>
              {tx.inviteClient} <ArrowRight size={14} strokeWidth={1.8} />
            </button>
          </div>
        )}
        {loading ? (
          <SkeletonList />
        ) : priorities.length === 0 ? (
          unlinked > 0 ? null : (
            <EmptyState
              title={tx.noPriorities}
              sub={tx.noPrioritiesSub}
              cta={tx.createMission}
              onCta={onCreateMission}
            />
          )
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {priorities.map((p, i) => (
              <PriorityRow key={p.mission_id || i} p={p} tx={tx} lang={lang} onClick={() => onPriorityClick?.(p)} />
            ))}
          </div>
        )}
      </div>

      {/* ═══ KPIs opérationnels ═══ */}
      <div style={{ marginBottom: 24 }}>
        <SectionEyebrow>{tx.kpiTitle}</SectionEyebrow>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(200px, 100%), 1fr))", gap: 12 }}>
          {KPI_ORDER.map((key) => (
            <KpiCard
              key={key}
              label={tx.kpis[key]}
              value={loading ? "—" : (kpis[key] ?? 0)}
              onClick={() => onKpiClick?.(key)}
            />
          ))}
          {artworksRequiringAction != null && (
            <KpiCard
              label={tx.kpis.artworks_requiring_action}
              value={loading ? "—" : artworksRequiringAction}
              onClick={() => onKpiClick?.("artworks_requiring_action")}
            />
          )}
        </div>
      </div>

      {/* ═══ Opportunités détectées (§5) — masqué si aucune ═══ */}
      {opps.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <SectionEyebrow>{tx.opportunitiesTitle}</SectionEyebrow>
          <p style={{ margin: "-6px 0 12px", fontSize: 12.5, color: C.gray, fontFamily: SANS, lineHeight: 1.55 }}>
            {tx.opportunitiesLead}
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {opps.map((op) => (
              <OpportunityCard
                key={op.fingerprint}
                op={op}
                tx={tx}
                onPropose={() => onProposeMission?.(op)}
                onDraft={() => onDraftMission?.(op)}
                onDismiss={() => onDismissOpportunity?.(op)}
                onSnooze={() => onSnoozeOpportunity?.(op)}
              />
            ))}
          </div>
        </div>
      )}

      {/* ═══ Clients à suivre ═══ */}
      <div style={{ marginBottom: 8 }}>
        <SectionEyebrow>{tx.clientsTitle}</SectionEyebrow>
        {loading ? (
          <SkeletonList rows={2} />
        ) : clients.length === 0 ? (
          <EmptyState
            title={tx.noClients}
            sub={tx.noClientsSub}
            cta={tx.inviteClient}
            onCta={onInviteClient}
          />
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(240px, 100%), 1fr))", gap: 12 }}>
            {clients.map((c, i) => (
              <ClientCard key={c.client_id || i} c={c} tx={tx} lang={lang} onClick={() => onClientClick?.(c.client_id)} />
            ))}
          </div>
        )}
      </div>

      {/* Réassurance discrète */}
      <p style={{ margin: "18px 0 0", fontSize: 12.5, color: C.gray, fontFamily: SANS, fontStyle: "italic", lineHeight: 1.6 }}>
        {tx.reassurance}
      </p>
    </div>
  );
}

/* ── Sous-composants ──────────────────────────────────────── */

function SecondaryAction({ icon: Icon, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: 0, background: "transparent", border: "none",
        color: C.goldDark, fontFamily: SANS, fontSize: 13, fontWeight: 500,
        cursor: "pointer",
      }}
    >
      <Icon size={15} strokeWidth={1.6} /> {label}
    </button>
  );
}

function SectionEyebrow({ children }) {
  return (
    <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.09em", color: C.goldDark, fontFamily: SANS, fontWeight: 600, marginBottom: 12 }}>
      {children}
    </div>
  );
}

function PriorityRow({ p, tx, lang, onClick }) {
  const meta = tx.kinds[p.kind] || tx.kinds.follow_up;
  const style = KIND_STYLE[p.kind] || KIND_STYLE.follow_up;
  const Icon = style.icon;
  const due = fmtDate(p.due_at, lang);
  const client = p.client_label || tx.clientFallback;
  return (
    <div
      onClick={onClick}
      style={{
        ...cardBase,
        display: "flex", alignItems: "center", gap: 14,
        padding: "12px 14px 12px 0", cursor: "pointer",
        borderLeft: `3px solid ${style.fg}`,
        overflow: "hidden",
      }}
    >
      <div style={{ width: 34, height: 34, marginLeft: 14, borderRadius: 7, background: style.bg, color: style.fg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon size={17} strokeWidth={1.8} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: "0.05em", color: style.fg, fontFamily: SANS, fontWeight: 600, marginBottom: 2 }}>
          {meta.label}
        </div>
        <div style={{ fontSize: 14.5, color: C.ink, fontFamily: SERIF, lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {p.title || meta.label}
        </div>
        <div style={{ fontSize: 12.5, color: C.gray, fontFamily: SANS, marginTop: 2 }}>
          {client}
          {due ? ` · ${tx.dueOn} ${due}` : ` · ${tx.noDue}`}
        </div>
      </div>
      <div style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "7px 12px", borderRadius: 7, background: C.cream, border: `1px solid ${C.stone}`, color: C.ink, fontFamily: SANS, fontSize: 12.5, fontWeight: 600, flexShrink: 0, marginRight: 14, whiteSpace: "nowrap" }}>
        {meta.cta} <ArrowRight size={14} strokeWidth={1.8} />
      </div>
    </div>
  );
}

function OpportunityCard({ op, tx, onPropose, onDraft, onDismiss, onSnooze }) {
  return (
    <div style={{ ...cardBase, padding: "16px 18px", borderLeft: `3px solid ${C.gold}` }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div style={{ width: 30, height: 30, borderRadius: 7, background: C.attnBg, color: C.goldDark, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2 }}>
          <Sparkles size={16} strokeWidth={1.7} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, color: C.ink, fontFamily: SERIF, lineHeight: 1.3 }}>{op.headline}</div>
          {op.context && (
            <div style={{ fontSize: 12.5, color: C.gray, fontFamily: SANS, marginTop: 3 }}>{op.context}</div>
          )}
          {op.body && (
            <div style={{ fontSize: 12.5, color: C.gray, fontFamily: SANS, marginTop: 5, lineHeight: 1.5 }}>{op.body}</div>
          )}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12, alignItems: "center" }}>
            <button
              type="button"
              onClick={onPropose}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px", background: C.ink, color: C.white, border: "none", borderRadius: 7, fontFamily: SANS, fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}
            >
              {tx.proposeMission} <ArrowRight size={13} strokeWidth={1.9} />
            </button>
            <button
              type="button"
              onClick={onDraft}
              style={{ padding: "8px 12px", background: C.cream, color: C.ink, border: `1px solid ${C.stone}`, borderRadius: 7, fontFamily: SANS, fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}
            >
              {tx.createDraft}
            </button>
            <span style={{ flex: 1 }} />
            <button
              type="button"
              onClick={onSnooze}
              title={tx.remindMe}
              style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "7px 10px", background: "transparent", color: C.gray, border: "none", borderRadius: 7, fontFamily: SANS, fontSize: 12, cursor: "pointer" }}
            >
              <Clock3 size={14} strokeWidth={1.6} /> {tx.remindMe}
            </button>
            <button
              type="button"
              onClick={onDismiss}
              title={tx.ignore}
              style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "7px 10px", background: "transparent", color: C.gray, border: "none", borderRadius: 7, fontFamily: SANS, fontSize: 12, cursor: "pointer" }}
            >
              <X size={14} strokeWidth={1.8} /> {tx.ignore}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function KpiCard({ label, value, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...cardBase,
        textAlign: "left", padding: "16px 16px 14px", cursor: "pointer",
        display: "flex", flexDirection: "column", gap: 6, minWidth: 0,
        transition: "border-color 0.15s, box-shadow 0.15s",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.gold; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.stone; }}
    >
      <span style={{ fontSize: 30, fontWeight: 400, color: C.ink, fontFamily: SERIF, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
        {value}
      </span>
      <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span style={{ fontSize: 12.5, color: C.gray, fontFamily: SANS, lineHeight: 1.35 }}>{label}</span>
        <ChevronRight size={15} strokeWidth={1.6} color={C.goldDark} style={{ flexShrink: 0 }} />
      </span>
    </button>
  );
}

function ClientCard({ c, tx, lang, onClick }) {
  const name = c.client_label || tx.clientFallback;
  const nextDue = fmtDate(c.next_due, lang);
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        ...cardBase, textAlign: "left", padding: "14px 16px", cursor: "pointer",
        display: "flex", flexDirection: "column", gap: 6,
        transition: "border-color 0.15s",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = C.gold; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = C.stone; }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <span style={{ fontSize: 15, color: C.ink, fontFamily: SERIF, lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{name}</span>
        {c.has_overdue && (
          <span style={{ fontSize: 10, fontFamily: SANS, fontWeight: 700, color: C.lateFg, background: C.lateBg, padding: "2px 7px", borderRadius: 999, flexShrink: 0, textTransform: "uppercase", letterSpacing: "0.04em" }}>
            {tx.overdueLabel}
          </span>
        )}
      </div>
      <div style={{ fontSize: 12.5, color: C.gray, fontFamily: SANS }}>
        {(c.collections_count != null || c.artworks_count != null)
          ? tx.colsArtworks(c.collections_count || 0, c.artworks_count || 0)
          : tx.missionsOpen(c.missions_open || 0)}
        {c.missions_open > 0 ? ` · ${tx.missionsOpen(c.missions_open)}` : ""}
        {nextDue ? ` · ${tx.nextDue} ${nextDue}` : ""}
      </div>
    </button>
  );
}

function EmptyState({ title, sub, cta, onCta }) {
  return (
    <div style={{ ...cardBase, padding: "22px 20px", background: C.cream }}>
      <div style={{ fontSize: 15, color: C.ink, fontFamily: SERIF, marginBottom: 5 }}>{title}</div>
      <div style={{ fontSize: 13, color: C.gray, fontFamily: SANS, lineHeight: 1.55, marginBottom: 14, maxWidth: 460 }}>{sub}</div>
      <button
        type="button"
        onClick={onCta}
        style={{ display: "inline-flex", alignItems: "center", gap: 7, padding: "9px 16px", background: C.ink, color: C.white, border: "none", borderRadius: 8, fontFamily: SANS, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
      >
        <Plus size={15} strokeWidth={2} /> {cta}
      </button>
    </div>
  );
}

function SkeletonList({ rows = 3 }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{ ...cardBase, height: 64, background: "linear-gradient(90deg,#f5f3ef 0%,#faf8f4 50%,#f5f3ef 100%)" }} />
      ))}
    </div>
  );
}
